import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClipboardList } from 'lucide-react'
import { listAuditLogs } from '../../../api/admin'
import { QueryBoundary } from '../../../components/common/QueryBoundary'
import { StatusBadge } from '../../../components/common/Status'
import { TimeAgo } from '../../../components/common/TimeAgo'
import { Skeleton } from '../../../components/ui/skeleton'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Button } from '../../../components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip'
import { formatAbsTs, formatNum } from '../../../lib/format'

const PAGE_SIZE = 20

export function AuditSection() {
  const [filters, setFilters] = useState({ actorId: '', action: '', regionCode: '' })
  const [applied, setApplied] = useState<typeof filters>({ actorId: '', action: '', regionCode: '' })
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['v2', 'audit', { ...applied, page }],
    queryFn: () =>
      listAuditLogs({
        actorId: applied.actorId || undefined,
        action: applied.action || undefined,
        regionCode: applied.regionCode || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    retry: 1,
  })

  const data = query.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="panel-glass overflow-hidden rounded-xl">
      <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
        {(
          [
            ['actorId', '操作者'],
            ['action', '操作类型'],
            ['regionCode', '区域'],
          ] as const
        ).map(([key, label]) => (
          <div key={key}>
            <Label htmlFor={`audit-${key}`}>{label}</Label>
            <Input
              id={`audit-${key}`}
              value={filters[key]}
              onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
              className="mt-1.5 w-40"
            />
          </div>
        ))}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setApplied(filters)
            setPage(1)
          }}
        >
          查询
        </Button>
      </div>

      <QueryBoundary
        query={query}
        skeleton={<div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>}
        isEmpty={(d) => d.items.length === 0}
        empty={{ title: '暂无审计记录', description: '所有非 GET 的 /api/v2 请求都会自动写入审计', icon: <ClipboardList size={24} /> }}
      >
        {(d) => (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">时间</th>
                    <th className="px-4 py-2.5 font-medium">操作者</th>
                    <th className="px-4 py-2.5 font-medium">操作</th>
                    <th className="px-4 py-2.5 font-medium">资源</th>
                    <th className="px-4 py-2.5 font-medium">区域</th>
                    <th className="px-4 py-2.5 font-medium">结果</th>
                    <th className="px-4 py-2.5 font-medium">详情</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                  {d.items.map((log) => (
                    <tr key={log.auditId} className="text-xs transition hover:bg-foreground/[.03]">
                      <td className="px-4 py-2.5">
                        <TimeAgo ts={log.createdAt} className="text-muted-foreground" />
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-foreground/90">{log.actorId}</td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-primary">{log.action}</td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                        {log.resourceType}
                        {log.resourceId ? `/${log.resourceId}` : ''}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{log.regionCode ?? '—'}</td>
                      <td className="px-4 py-2.5">
                        <StatusBadge
                          status={log.outcome === 'denied' ? 'rejected' : 'confirmed'}
                          labels={{ rejected: '拒绝', confirmed: '成功' }}
                        />
                      </td>
                      <td className="max-w-[200px] px-4 py-2.5">
                        {log.details ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block cursor-help truncate font-mono text-[10px] text-muted-foreground">
                                {formatAbsTs(log.createdAt)} · {log.requestId}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm break-all font-mono">{log.details}</TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-muted-foreground/40">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              <span>共 {formatNum(d.total)} 条 · 第 {page}/{totalPages} 页</span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>上一页</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>下一页</Button>
              </div>
            </div>
          </>
        )}
      </QueryBoundary>
    </div>
  )
}
